import React, { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonGroup, Text } from '@fragments';
import { IconAlertTriangle, IconBookmarkPlus, IconCheck, IconCopy, IconDownload, IconFileText, IconMovie, IconPhoto, IconVolumeOff, IconX } from '@tabler/icons-react';
import Popover from '../Popover';
import PageNavigator from '../PageNavigator';
import { layerName } from '../ToolPanel/panels/LayersPanel';
import { pageIndex } from '../../../model/operations';
import { hasVideo } from '../../../model/timeline';
import './styles.scss';

const RailButton = forwardRef(function RailButton({ icon: Icon, label, badge, disabled, active, ...props }, ref) {
  return <button ref={ref} type="button" className={`art-tool art-action ${active ? 'is-active' : ''}`} disabled={disabled} aria-label={badge ? `${label} (${badge})` : label} {...props}>
    <Icon aria-hidden="true" />
    <Text.caption contrast={disabled ? 5 : 7}>{label}</Text.caption>
    {badge && <span className="art-action__badge">{badge}</span>}
  </button>;
});

// A carousel downloads all its pages (one .zip, in order) or the page on the stage.
function ExportPanel({ doc, pageId, exporter, overflow, emptyPlaces = [], onSelectLayer }) {
  const { t } = useTranslation('artEditor');
  const [format, setFormat] = useState('png');
  const [scale, setScale] = useState(1);
  const [pages, setPages] = useState('all');
  const many = doc.pages.length > 1;
  const number = pageIndex(doc, pageId) + 1;
  const sizes = [1, 2].map(value => ({ value, label: `${doc.width * value} × ${doc.height * value}` }));
  const which = many ? pages : 'current';
  const flagged = which === 'all' ? overflow : overflow.filter(layer => layer.pageId === pageId);
  // 2.10.0: image places still empty download as their drawing; the creator is told first.
  const empty = which === 'all' ? emptyPlaces : emptyPlaces.filter(layer => layer.pageId === pageId);
  const where = layer => (many ? `${t('pageNumber', { number: pageIndex(doc, layer.pageId) + 1 })} · ${layerName(layer, t)}` : layerName(layer, t));
  // 2.13.0: pages with videos download as MP4; the format and size are the other pages'.
  const videoPages = (which === 'all' ? doc.pages : doc.pages.filter(page => page.id === pageId)).filter(hasVideo).length;
  const stills = (which === 'all' ? doc.pages.length : 1) - videoPages;
  const progress = exporter.progress;
  return <>
    {many && <div className="art-export__field"><Text.caption contrast={6}>{t('pages')}</Text.caption>
      <ButtonGroup size="small" grow value={pages} onChange={setPages}
        options={[{ value: 'all', label: t('exportAllPages', { count: doc.pages.length }) }, { value: 'current', label: t('pageNumber', { number }) }]} />
      {pages === 'all' && <Text.caption contrast={5}>{t('exportZipHint', { count: doc.pages.length })}</Text.caption>}
    </div>}
    {stills > 0 && <>
      <div className="art-export__field"><Text.caption contrast={6}>{t('fileFormat')}</Text.caption>
        <ButtonGroup size="small" grow value={format} onChange={setFormat} options={[{ value: 'png', label: 'PNG' }, { value: 'jpeg', label: 'JPG' }]} /></div>
      <div className="art-export__field"><Text.caption contrast={6}>{t('fileSize')}</Text.caption>
        <ButtonGroup size="small" grow value={scale} onChange={setScale} options={sizes} /></div>
    </>}
    {videoPages > 0 && <div className="art-export__note" role="note">
      <Text.caption contrast={7} left={<IconMovie />}>{t(stills > 0 ? 'exportVideoPages' : 'exportVideoPage', { count: videoPages, width: doc.width, height: doc.height })}</Text.caption>
    </div>}
    {progress && <div className="art-export__progress" role="status">
      <Text.caption contrast={7}>{t('exportVideoProgress', { number: progress.page, percent: Math.round(progress.value * 100) })}</Text.caption>
      <span className="art-export__bar" aria-hidden="true"><span style={{ transform: `scaleX(${Math.max(0.02, progress.value)})` }} /></span>
    </div>}
    {exporter.audio === 'dropped' && !exporter.busy && <Text.caption contrast={7} left={<IconVolumeOff />} role="status">{t('exportVideoSilent')}</Text.caption>}
    {flagged.length > 0 && <div className="art-export__warning" role="alert">
      <Text.caption contrast={8} left={<IconAlertTriangle />}>{t('overflowExport', { count: flagged.length })}</Text.caption>
      {flagged.slice(0, 3).map(layer => <Button key={layer.id} size="small" variant="text" color="overlay" preventLoading onClick={() => onSelectLayer(layer)}>
        {where(layer)}</Button>)}
    </div>}
    {empty.length > 0 && <div className="art-export__warning" role="alert">
      <Text.caption contrast={8} left={<IconPhoto />}>{t('emptyPlacesExport', { count: empty.length })}</Text.caption>
      {empty.slice(0, 3).map(layer => <Button key={layer.id} size="small" variant="text" color="overlay" preventLoading onClick={() => onSelectLayer(layer)}>{where(layer)}</Button>)}
    </div>}
    {exporter.error && <div className="art-export__error" role="alert"><Text.caption contrast={8}>{t(`exportError_${exporter.error}`)}</Text.caption></div>}
    {exporter.done && !exporter.error && <Text.caption contrast={6} left={<IconCheck />} role="status">{t(exporter.done === 'stored' ? 'exportStored' : 'exportDownloaded')}</Text.caption>}
    <Button fullWidth color="inverse" variant="rounded" startIcon={<IconDownload />} loading={exporter.busy} disabled={exporter.busy} preventLoading
      onClick={() => exporter.run({ format, scale, pages: which, pageId })}>{t(flagged.length || empty.length ? 'downloadAnyway' : 'download')}</Button>
  </>;
}

function CaptionPanel({ caption, onCaptionChange }) {
  const { t } = useTranslation('artEditor');
  const [text, setText] = useState(caption?.text || '');
  const [copied, setCopied] = useState(false);
  useEffect(() => { setText(caption?.text || ''); }, [caption?.text]);
  const hashtags = (caption?.hashtags || []).map(tag => `#${tag.replace(/^#/, '')}`).join(' ');
  const full = [text.trim(), hashtags].filter(Boolean).join('\n\n');
  const copy = async () => {
    try { await navigator.clipboard.writeText(full); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { setCopied(false); }
  };
  return <>
    <div className="art-popover__head"><Text.caption contrast={6}>{t('captionLength', { count: text.length })}</Text.caption>
      <Button size="small" variant="text" color="overlay" preventLoading startIcon={copied ? <IconCheck /> : <IconCopy />} disabled={!full} onClick={copy}>{t(copied ? 'copied' : 'copy')}</Button>
    </div>
    <textarea className="art-field is-caption" rows={7} value={text} maxLength={2200} readOnly={!onCaptionChange} aria-label={t('caption')} placeholder={t('captionEmpty')}
      onChange={event => setText(event.target.value)} onBlur={() => { if (onCaptionChange && text !== (caption?.text || '')) onCaptionChange({ ...(caption || { hashtags: [] }), text }); }} />
    {hashtags && <Text.caption contrast={6}>{hashtags}</Text.caption>}
    <Text.caption contrast={5}>{t('captionHint')}</Text.caption>
  </>;
}

// The right rail: the host's close (at the very top), the profile the post is for, download
// (format and size), the caption, "Salvar como modelo" (`onSaveTemplate`) and the host's
// actions (publishing, automations), which may be marked "soon"; at its foot, a carousel's
// pages (`pages`, see PageNavigator).
export default function ActionRail({ doc, pageId, profile, exporter, overflow, emptyPlaces = [], onSelectLayer, caption, onCaptionChange, actions = [], close = null, pages = null, onSaveTemplate = null }) {
  const { t } = useTranslation('artEditor');
  const initial = (profile?.name || profile?.handle || '?').replace('@', '').slice(0, 1).toUpperCase();
  return <aside className="art-actions" aria-label={t('actions')}>
    {close && <Button className="art-actions__close" color="overlay" variant="rounded" size="medium" preventLoading icon={<IconX />}
      aria-label={close.label} tooltip={close.label} onClick={close.onClick} />}
    {profile && <div className="art-actions__profile" title={profile.handle ? `@${profile.handle.replace('@', '')}` : profile.name}>
      {profile.avatar || (profile.picture ? <img src={profile.picture} alt={profile.handle || profile.name || ''} /> : <span aria-hidden="true">{initial}</span>)}
    </div>}
    {doc && <Popover placement="left-start" width={288} label={t('downloadArt')} dock trigger={<RailButton icon={IconDownload} label={t('download')} />}>
      <ExportPanel doc={doc} pageId={pageId} exporter={exporter} overflow={overflow} emptyPlaces={emptyPlaces} onSelectLayer={onSelectLayer} />
    </Popover>}
    {(caption || onCaptionChange) && <Popover placement="left-start" width={320} label={t('caption')} dock trigger={<RailButton icon={IconFileText} label={t('caption')} />}>
      <CaptionPanel caption={caption} onCaptionChange={onCaptionChange} />
    </Popover>}
    {onSaveTemplate && <RailButton icon={IconBookmarkPlus} label={t('saveTemplate')} title={t('saveTemplateTitle')} onClick={onSaveTemplate} />}
    {actions.map(action => <RailButton key={action.id} icon={action.icon} label={action.label} badge={action.badge} disabled={action.disabled}
      title={action.tooltip || action.label} onClick={action.onClick} />)}
    {pages && <PageNavigator {...pages} />}
  </aside>;
}
